import { Dispatch, FC, SetStateAction } from 'react';

import Image from 'next/image';

import classNames from 'classnames';

import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';

import { TypeProduct } from '@/core/generated-types/TypeProduct';

import {
  SubEngineInstallmentStatusEnum,
  TypeSubEngineInstallment,
} from '@/lib/api/subscriptionManager/types';

import interpolate from '@/utils/translations/interpolate';
import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';

import { useAccordion } from '@/utils/useAccordion';

import ShipDate from './components/ShipDate';
import { Badge } from '@/components/ui';
import UnpauseERCButton from '@/components/account/UnpauseERCButton';

export interface ProductInfoProps {
  t: TypeAccountERCTranslations;
  product: TypeProduct;
  installment?: TypeSubEngineInstallment;
  setInstallment?: Dispatch<SetStateAction<TypeSubEngineInstallment>>;
  onlyCollapsed?: boolean;
  isCurrentBox?: boolean;
  isNextOrder?: boolean;
  isUpcomingBox?: boolean;
  isPausedBox?: boolean;
}

const ProductInfo: FC<ProductInfoProps> = ({
  t,
  product,
  installment,
  setInstallment,
  onlyCollapsed,
  isCurrentBox,
  isNextOrder,
  isUpcomingBox,
  isPausedBox,
}) => {
  const isSkippedBox =
    installment?.status === SubEngineInstallmentStatusEnum.SKIPPED;

  const shouldRenderUnpauseButton = installment && isPausedBox;

  const { Icon, Accordion } = useAccordion();

  const CollapsedContent = () => (
    <>
      <div
        className={classNames(
          'grid gap-x-4 mb:gap-x-6 md:items-center mb-6 md:mb-8',
          {
            'md:grid-cols-[auto_1fr] gap-y-6': isCurrentBox,
            'grid-cols-[auto_1fr]': isNextOrder || isUpcomingBox || isPausedBox,
            'items-start': isPausedBox,
          },
        )}
      >
        <Image
          className={classNames('bg-blue-light object-cover', {
            'w-full md:w-64': isCurrentBox,
            'size-[88px] md:size-[140px]': isNextOrder || isUpcomingBox,
            'grayscale brightness-90 opacity-50': isSkippedBox,
          })}
          width={140}
          height={140}
          src={`https:${product.fields?.featuredImage.fields.Image.fields.file.url}`}
          alt={product.fields.title}
        />

        <div className="grid grid-cols-[1fr_auto] gap-x-4 mb:gap-x-6">
          <div className="flex flex-col-reverse md:flex-row md:justify-between md:items-center">
            <div className="grid gap-y-1">
              {!isCurrentBox && installment && (
                <ShipDate
                  t={t}
                  installment={installment}
                  setInstallment={setInstallment}
                  isSkippedBox={isSkippedBox}
                  isPausedBox={isPausedBox}
                />
              )}

              <div className="text-left">
                <Heading
                  tag={isCurrentBox ? HeadingTag.H2 : HeadingTag.H3}
                  size={isCurrentBox ? HeadingSize.H2 : HeadingSize.H3}
                  classes={classNames({
                    'text-grey-midtone': isSkippedBox,
                  })}
                  text={product.fields.title}
                />

                <p
                  className={classNames({
                    'text-grey-midtone': isSkippedBox,
                    'md:text-lg': isCurrentBox,
                    'text-sm md:text-base': !isCurrentBox,
                  })}
                >
                  {product.fields.shortDescription}
                </p>
              </div>

              {shouldRenderUnpauseButton && (
                <div
                  className="min-w-[157px] hidden md:flex"
                  data-testid="unpause-desktop-button"
                >
                  <UnpauseERCButton
                    t={t}
                    ercSubscriptionId={installment?.subscription_id}
                  />
                </div>
              )}
            </div>

            {isSkippedBox && (
              <Badge
                badgeColor="GreyMidtone"
                text={t.skippedBoxBadge}
                classes="bg-grey-midtone max-h-[23px] md:max-h-[30px] !text-xs !md:text-sm mb-2 md:mb-0"
              />
            )}
          </div>

          {!onlyCollapsed && (
            <div className="flex items-center">
              <Icon
                className={classNames({ 'text-grey-midtone': isSkippedBox })}
              />
            </div>
          )}
        </div>
      </div>

      {shouldRenderUnpauseButton && (
        <div
          className="w-full flex md:hidden"
          data-testid="unpause-mobile-button"
        >
          <UnpauseERCButton
            t={t}
            fullWidth
            ercSubscriptionId={installment.subscription_id}
          />
        </div>
      )}
    </>
  );

  const ExpandedContent = () => (
    <div className="grid gap-y-4 mb-6 md:mb-8">
      <div className="grid gap-y-4">
        <Heading
          tag={HeadingTag.H4}
          size={HeadingSize.H4}
          text={interpolate(t.insideTitle, {
            shortDescription:
              product.fields.shortDescription?.toLowerCase() || '',
          })}
        />

        <div className="flex flex-row gap-x-2 items-start overflow-x-auto scroll-smooth">
          {product.fields.subProducts?.map((subProduct, index) => (
            <div
              key={`sub-product-${index}`}
              className="grid gap-y-2 w-[88px] md:w-[104px] min-w-[88px] md:min-w-[104px]"
            >
              <Image
                className="size-[88px] md:size-[104px]"
                width={104}
                height={104}
                src={`https:${subProduct.fields.image.fields.file.url}`}
                alt={subProduct.fields.title}
              />

              <p className="text-xs font-bold">{subProduct.fields.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (onlyCollapsed || isSkippedBox) {
    return (
      <div
        data-testid="productInfo-onlyCollapsed"
        className={classNames({
          'h-full border-b border-y-charcoal-opacity-20': isSkippedBox,
        })}
      >
        <CollapsedContent />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div data-testid="productInfo">
      <Accordion
        collapsedContent={<CollapsedContent />}
        expandedContent={<ExpandedContent />}
      />
    </div>
  );
};

export default ProductInfo;
